import React, { useEffect, useState } from "react";
import axios from "axios";
import { Save, RefreshCw, Image, AlignLeft, Plus, Trash2, List } from "lucide-react";

const ManageAboutSection = () => {
  const [formData, setFormData] = useState({
    isActive: true,
    mainImage: "",
    subtitle: "",
    mainTitle: "",
    description: "",
    features: [],
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "" });

  // Fetch Data
  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await axios.get("https://allyoutube-01-02d59d78beb5.herokuapp.com/api/about-section");
      setFormData(res.data.data);
    } catch (error) {
      setMessage({ text: "Error loading data", type: "danger" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Handle Input Change for simple fields
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  // Handle Feature Array Changes
  const handleFeatureChange = (index, field, value) => {
    const updatedFeatures = [...formData.features];
    updatedFeatures[index][field] = value;
    setFormData((prev) => ({ ...prev, features: updatedFeatures }));
  };

  const addFeature = () => {
    setFormData((prev) => ({
      ...prev,
      features: [
        ...prev.features,
        { icon: "", title: "New Feature", text: "Description goes here..." },
      ],
    }));
  };

  const removeFeature = (index) => {
    setFormData((prev) => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index),
    }));
  };

  // Submit Data
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put("https://allyoutube-01-02d59d78beb5.herokuapp.com/api/about-section", formData);
      setMessage({ text: "About section updated successfully!", type: "success" });
    } catch (error) {
      setMessage({ text: "Failed to update section.", type: "danger" });
    } finally {
      setSaving(false);
    }
  };

  // Reset Data
  const handleReset = async () => {
    if (window.confirm("Are you sure you want to reset to default settings?")) {
      try {
        await axios.post("https://allyoutube-01-02d59d78beb5.herokuapp.com/api/about-section/reset");
        fetchData();
        setMessage({ text: "Reset to defaults.", type: "info" });
      } catch (error) {
        setMessage({ text: "Reset failed.", type: "danger" });
      }
    }
  };

  if (loading) return <div className="text-center mt-5">Loading Admin Panel...</div>;

  return (
    <div className="container py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="fw-bold"><List className="me-2" size={28} /> Manage About Section</h2>
        <button className="btn btn-outline-secondary" onClick={handleReset}>
          <RefreshCw size={18} className="me-1" /> Reset Defaults
        </button>
      </div>

      {message.text && (
        <div className={`alert alert-${message.type} alert-dismissible fade show`} role="alert">
          {message.text}
          <button type="button" className="btn-close" onClick={() => setMessage({ text: "", type: "" })}></button>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        
        {/* Main Visuals & Text */}
        <div className="row mb-4">
          <div className="col-lg-6 mb-3">
            <div className="card shadow-sm h-100">
              <div className="card-header bg-dark fw-bold"><Image className="me-2" size={20} /> Visuals</div>
              <div className="card-body">
                <div className="mb-3">
                  <label className="form-label text-dark">Main Image URL</label>
                  <input
                    type="text" className="form-control" name="mainImage"
                    value={formData.mainImage} onChange={handleChange}
                    placeholder="assets/images/about/01.png"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="col-lg-6 mb-3">
            <div className="card shadow-sm h-100">
              <div className="card-header bg-dark fw-bold"><AlignLeft className="me-2" size={20} /> Headings</div>
              <div className="card-body">
                <div className="mb-3">
                  <label className="form-label text-dark">Subtitle</label>
                  <input type="text" className="form-control" name="subtitle" value={formData.subtitle} onChange={handleChange} />
                </div>
                <div className="mb-3">
                  <label className="form-label text-dark">Main Title</label>
                  <input type="text" className="form-control" name="mainTitle" value={formData.mainTitle} onChange={handleChange} />
                </div>
              </div>
            </div>
          </div>

          <div className="col-12">
            <div className="card shadow-sm">
              <div className="card-header bg-dark w-bold"><AlignLeft className="me-2" size={20} /> Description</div>
              <div className="card-body">
                <div className="mb-3">
                  <label className="form-label text-dark">Main Description Text</label>
                  <textarea className="form-control" name="description" rows="3" value={formData.description} onChange={handleChange}></textarea>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Features List */}
        <div className="row mb-4">
          <div className="col-12">
            <div className="card shadow-sm">
              <div className="card-header bg-dark d-flex justify-content-between align-items-center fw-bold">
                <span><List className="me-2 text-white" size={20} /> Feature Items</span>
                <button type="button" className="btn btn-sm btn-primary" onClick={addFeature}>
                  <Plus size={16} className="me-1"/> Add Feature
                </button>
              </div>
              <div className="card-body">
                {formData.features.map((feature, index) => (
                  <div key={index} className="card bg-light mb-3 p-3 border-0">
                    <div className="d-flex justify-content-between mb-2 text-dark">
                      <strong>Feature #{index + 1}</strong>
                      <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => removeFeature(index)}>
                        <Trash2 size={14} />
                      </button>
                    </div>
                    <div className="row g-2">
                      <div className="col-md-3">
                        <label className="small text-muted">Icon Image URL</label>
                        <input type="text" className="form-control form-control-sm" value={feature.icon} onChange={(e) => handleFeatureChange(index, "icon", e.target.value)} placeholder="assets/..." />
                      </div>
                      <div className="col-md-3">
                        <label className="small text-muted">Title</label>
                        <input type="text" className="form-control form-control-sm" value={feature.title} onChange={(e) => handleFeatureChange(index, "title", e.target.value)} placeholder="Title" />
                      </div>
                      <div className="col-md-6">
                        <label className="small text-muted">Description</label>
                        <textarea className="form-control form-control-sm" rows="1" value={feature.text} onChange={(e) => handleFeatureChange(index, "text", e.target.value)} placeholder="Short description"></textarea>
                      </div>
                    </div>
                  </div>
                ))}
                {formData.features.length === 0 && <p className="text-muted text-center">No features added.</p>}
              </div>
            </div>
          </div>
        </div>

        <div className="d-flex justify-content-end gap-2">
          <button type="submit" className="btn btn-success px-4" disabled={saving}>
            {saving ? "Saving..." : <><Save size={18} className="me-2" /> Save Changes</>}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ManageAboutSection;